import { Outlet, Link, createRootRouteWithContext, useParams, useRouterState } from "@tanstack/react-router";
import useAuth from "../hooks/useAuth";
import { AuthType } from "../context/AuthContext";
import {
  LayoutDashboard,
  Building2,
  PlusSquare,
  ClipboardList,
  Map,
  Calendar,
  Users,
  Settings,
  UserCog,
  Headphones,
  ExternalLink,
  LogOut,
  FilePlus,
  ShieldCheck,
  StickyNote,
  BookOpen,
  History,
} from "lucide-react";
import { ReactNode, useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Logo from "../components/Logo/Logo";
import HelpButton from "../components/Help/HelpButton";

/**
 * Sidebar admina/konsultanta — rozszerzony (240px) z ikonami i etykietami.
 * Styl zbliżony do Claude Code (odcienie szarości, minimalizm).
 * - Tło: bg-white z cienką ramką po prawej.
 * - Aktywna pozycja: bg-gray-100 + font-medium.
 * - Hover: bg-gray-50.
 * - Sekcje oddzielone cienką linią + nagłówkiem tekstowym.
 * - Na dole: avatar z inicjałem roli + nazwa roli + tenant.
 * - Wyloguj jako ostatnia pozycja w stylu przycisku.
 */

type NavItem = {
  to: string;
  params?: Record<string, string>;
  label: string;
  icon: ReactNode;
  matchPath?: string;
};

function SidebarLink({
  item,
  activePath,
  collapsed,
}: {
  item: NavItem;
  activePath: string;
  collapsed: boolean;
}) {
  const expectedPath = item.matchPath ?? item.to;
  const isActive = activePath === expectedPath;
  return (
    <Link
      to={item.to as any}
      params={item.params as any}
      title={collapsed ? item.label : undefined}
      className={`flex items-center gap-3 ${
        collapsed ? "justify-center px-2" : "px-3"
      } py-1.5 rounded-md text-sm transition-colors ${
        isActive
          ? "bg-[rgb(239,238,235)] text-gray-900 font-medium"
          : "text-gray-700 hover:bg-[rgb(239,238,235)] hover:text-gray-900"
      }`}>
      <span className="shrink-0 text-gray-500">{item.icon}</span>
      {!collapsed && <span className="truncate">{item.label}</span>}
    </Link>
  );
}

function SidebarSectionHeader({ children }: { children: ReactNode }) {
  return (
    <div className="text-xs font-medium text-gray-400 uppercase tracking-wider px-3 pt-4 pb-1">
      {children}
    </div>
  );
}

function roleLabel(auth: AuthType): string {
  if (auth.isSuperadmin) return "Superadmin";
  if (auth.isAdmin) return "Administrator";
  if (auth.isConsultant) return "Konsultant";
  if (auth.isInhabitant) return "Mieszkaniec";
  return "Użytkownik";
}

function roleInitial(auth: AuthType): string {
  if (auth.isSuperadmin) return "S";
  if (auth.isAdmin) return "A";
  if (auth.isConsultant) return "K";
  if (auth.isInhabitant) return "M";
  return "U";
}

export const Dashboard = () => {
  const { logout, auth } = useAuth();
  const { isAdmin, isInhabitant, isLoggedIn, tenantId, isSuperadmin, isConsultant } = auth;

  const params = useParams({ strict: false }) as { tenant?: string };
  const urlTenant = params.tenant;
  const effectiveTenantId = tenantId !== "public" ? tenantId : urlTenant || "";
  const isTenantUser = effectiveTenantId && effectiveTenantId !== "public" && effectiveTenantId !== "admin";

  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const tenantPath = (p: string) => p.replace("$tenant", effectiveTenantId || "");

  // Sidebar collapse — preferencja zapamiętywana w localStorage żeby user
  // nie musiał klikać przy każdym wejściu. W trybie collapsed sidebar ma
  // 56px (tylko ikony z tooltipem), zamiast 240px (ikony + etykiety).
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem("sidebar_collapsed") === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("sidebar_collapsed", sidebarCollapsed ? "1" : "0");
    } catch { /* ignore */ }
  }, [sidebarCollapsed]);

  const superadminItems: NavItem[] = [
    { to: "/admin/tenant", label: "Gminy", icon: <Building2 className="h-4 w-4" /> },
    { to: "/admin/create-tenant", label: "Dodaj gminę", icon: <PlusSquare className="h-4 w-4" /> },
    { to: "/admin/superadmins", label: "Superadmini", icon: <ShieldCheck className="h-4 w-4" /> },
    { to: "/admin/help-articles", label: "Artykuły pomocy", icon: <BookOpen className="h-4 w-4" /> },
  ];

  const tenantAdminItems: NavItem[] = isTenantUser
    ? [
        {
          to: "/$tenant/dashboard",
          params: { tenant: effectiveTenantId },
          label: "Pulpit",
          icon: <LayoutDashboard className="h-4 w-4" />,
          matchPath: tenantPath("/$tenant/dashboard"),
        },
        {
          to: "/$tenant/procedures",
          params: { tenant: effectiveTenantId },
          label: "Procedury",
          icon: <ClipboardList className="h-4 w-4" />,
          matchPath: tenantPath("/$tenant/procedures"),
        },
        {
          to: "/$tenant/create-procedure",
          params: { tenant: effectiveTenantId },
          label: "Dodaj procedurę",
          icon: <FilePlus className="h-4 w-4" />,
          matchPath: tenantPath("/$tenant/create-procedure"),
        },
        {
          to: "/$tenant/applications",
          params: { tenant: effectiveTenantId },
          label: "Złożone wnioski",
          icon: <ClipboardList className="h-4 w-4" />,
          matchPath: tenantPath("/$tenant/applications"),
        },
        {
          to: "/$tenant/applications-map",
          params: { tenant: effectiveTenantId },
          label: "Mapa wniosków",
          icon: <Map className="h-4 w-4" />,
          matchPath: tenantPath("/$tenant/applications-map"),
        },
        {
          to: "/$tenant/consultation-admin",
          params: { tenant: effectiveTenantId },
          label: "Konsultacje",
          icon: <Calendar className="h-4 w-4" />,
          matchPath: tenantPath("/$tenant/consultation-admin"),
        },
        {
          to: "/$tenant/manage-consultants",
          params: { tenant: effectiveTenantId },
          label: "Konsultanci",
          icon: <Users className="h-4 w-4" />,
          matchPath: tenantPath("/$tenant/manage-consultants"),
        },
        {
          to: "/$tenant/consultant-search-log",
          params: { tenant: effectiveTenantId },
          label: "Rejestr wyszukiwań",
          icon: <History className="h-4 w-4" />,
          matchPath: tenantPath("/$tenant/consultant-search-log"),
        },
        {
          to: "/$tenant/edit",
          params: { tenant: effectiveTenantId },
          label: "Ustawienia gminy",
          icon: <Settings className="h-4 w-4" />,
          matchPath: tenantPath("/$tenant/edit"),
        },
        {
          to: "/$tenant/help-articles",
          params: { tenant: effectiveTenantId },
          label: "Artykuły pomocy",
          icon: <BookOpen className="h-4 w-4" />,
          matchPath: tenantPath("/$tenant/help-articles"),
        },
      ]
    : [];

  const consultantItems: NavItem[] = isTenantUser
    ? [
        {
          to: "/$tenant/consultant-dashboard",
          params: { tenant: effectiveTenantId },
          label: "Panel konsultanta",
          icon: <Headphones className="h-4 w-4" />,
          matchPath: tenantPath("/$tenant/consultant-dashboard"),
        },
        {
          to: "/$tenant/consultant-map",
          params: { tenant: effectiveTenantId },
          label: "Mapa konsultanta",
          icon: <StickyNote className="h-4 w-4" />,
          matchPath: tenantPath("/$tenant/consultant-map"),
        },
      ]
    : [];

  const inhabitantItems: NavItem[] = isTenantUser
    ? [
        {
          to: "/$tenant/edit-me",
          params: { tenant: effectiveTenantId },
          label: "Moje dane",
          icon: <UserCog className="h-4 w-4" />,
          matchPath: tenantPath("/$tenant/edit-me"),
        },
      ]
    : [];

  const publicPageItem: NavItem | null = isTenantUser
    ? {
        to: "/$tenant",
        params: { tenant: effectiveTenantId },
        label: "Strona publiczna gminy",
        icon: <ExternalLink className="h-4 w-4" />,
        matchPath: tenantPath("/$tenant"),
      }
    : null;

  return (
    <>
      <aside
        className={`sticky top-0 max-h-screen h-screen ${
          sidebarCollapsed ? "w-14" : "w-60"
        } bg-[#f8f8f6] border-r border-gray-200 flex flex-col shrink-0 transition-[width] duration-150`}>
        {/* Logo / nazwa aplikacji + przycisk zwijania */}
        <div className="h-16 flex items-center gap-3 px-3 relative">
          <Logo className="text-gray-700 shrink-0" size={sidebarCollapsed ? 28 : 36} />
          {!sidebarCollapsed && (
            <span className="text-base font-semibold text-gray-900 leading-tight">
              Wniosek<br />DoPlanu
            </span>
          )}
          <button
            type="button"
            onClick={() => setSidebarCollapsed((c) => !c)}
            title={sidebarCollapsed ? "Rozwiń menu" : "Zwiń menu"}
            className="absolute -right-3 top-5 w-6 h-6 rounded-full bg-white border border-gray-300 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:border-gray-500 shadow-sm z-10">
            {sidebarCollapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronLeft className="h-3.5 w-3.5" />}
          </button>
        </div>

        {/* Nawigacja */}
        <nav className="flex-1 overflow-y-auto px-2 py-3">
          {isSuperadmin && (
            <>
              {!sidebarCollapsed && <SidebarSectionHeader>Panel globalny</SidebarSectionHeader>}
              <div className="flex flex-col gap-0.5">
                {superadminItems.map((item) => (
                  <SidebarLink key={item.to} item={item} activePath={currentPath} collapsed={sidebarCollapsed} />
                ))}
              </div>
            </>
          )}

          {(isAdmin || isSuperadmin) && tenantAdminItems.length > 0 && (
            <>
              {!sidebarCollapsed && <SidebarSectionHeader>Gmina</SidebarSectionHeader>}
              <div className={`flex flex-col gap-0.5 ${sidebarCollapsed ? "mt-2" : ""}`}>
                {tenantAdminItems.map((item) => (
                  <SidebarLink key={item.to} item={item} activePath={currentPath} collapsed={sidebarCollapsed} />
                ))}
              </div>
            </>
          )}

          {(isConsultant || isAdmin || isSuperadmin) && consultantItems.length > 0 && (
            <>
              {!sidebarCollapsed && <SidebarSectionHeader>Konsultacje</SidebarSectionHeader>}
              <div className={`flex flex-col gap-0.5 ${sidebarCollapsed ? "mt-2" : ""}`}>
                {consultantItems.map((item) => (
                  <SidebarLink key={item.to} item={item} activePath={currentPath} collapsed={sidebarCollapsed} />
                ))}
              </div>
            </>
          )}

          {isInhabitant && inhabitantItems.length > 0 && (
            <div className="flex flex-col gap-0.5 mt-2">
              {inhabitantItems.map((item) => (
                <SidebarLink key={item.to} item={item} activePath={currentPath} collapsed={sidebarCollapsed} />
              ))}
            </div>
          )}

          {publicPageItem && (
            <>
              {!sidebarCollapsed && <SidebarSectionHeader>Widok publiczny</SidebarSectionHeader>}
              <div className={`flex flex-col gap-0.5 ${sidebarCollapsed ? "mt-2" : ""}`}>
                <SidebarLink item={publicPageItem} activePath={currentPath} collapsed={sidebarCollapsed} />
              </div>
            </>
          )}
        </nav>

        {/* Info o zalogowanym użytkowniku + wyloguj */}
        {isLoggedIn && (
          <div className={`${sidebarCollapsed ? "p-2" : "p-3"} flex flex-col gap-2`}>
            {!sidebarCollapsed && (
              <div className="flex items-center gap-2.5 px-1 py-1">
                <div className="w-8 h-8 rounded-full bg-gray-200 border border-gray-300 flex items-center justify-center text-gray-700 font-semibold text-sm shrink-0">
                  {roleInitial(auth)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{roleLabel(auth)}</div>
                  <div className="text-xs text-gray-500 truncate">
                    {effectiveTenantId ? `gmina: ${effectiveTenantId}` : "panel globalny"}
                  </div>
                </div>
              </div>
            )}
            <button
              onClick={() => logout()}
              title={sidebarCollapsed ? "Wyloguj się" : undefined}
              className={`flex items-center ${
                sidebarCollapsed ? "justify-center px-2" : "gap-2 px-3"
              } py-1.5 rounded-md text-sm text-gray-700 hover:bg-[rgb(239,238,235)] hover:text-gray-900 transition-colors`}>
              <LogOut className="h-4 w-4 text-gray-500" />
              {!sidebarCollapsed && <span>Wyloguj się</span>}
            </button>
          </div>
        )}
      </aside>
      <main className="flex-1 min-w-0 bg-gray-50 overflow-auto">
        <Outlet />
      </main>
    </>
  );
};

const Root = () => {
  return (
    <div className="h-full w-full min-h-screen bg-white flex">
      <Outlet />
      {/* Floating Help button — widoczny na każdej stronie. Otwiera drawer
          z artykułami pomocy filtrowanymi po roli użytkownika i kontekście
          aktualnej strony. Skrót `?` toggluje. */}
      <HelpButton />
    </div>
  );
};

export const RootRoute = createRootRouteWithContext<AuthType>()({
  component: () => <Root />,
});
